import { Box, Divider, Stack, Toolbar } from "@mui/material";
import { CustomLogo } from "components/images/Logo";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AppRoutes,
  ButtonsColorType,
  ButtonsType,
  CampaignPurpose,
} from "../../types/enums";
import { ProfileButton } from "../profile";
import { ReuseButton } from "../ui";
import AddIcon from "@mui/icons-material/Add";
import { ResyncDataBtn } from "../ui/ResyncDataBtn";
import { useGetUserQuery, useLazyGetCampaignSummaryQuery } from "../../api";
import { useEffect, useState } from "react";
import { pushUserDataToGTM } from "../../utils/pushUserDataToGTM";
import { NotificationBtn, ShareCampaignBtn } from "../ui/buttons";
import { DemoBlock } from "./DemoBlock";
import {
  useAppSelector,
  useGetUserShareCampaignRoles,
  useNotFoundRedirect,
  useScrollbarWidth,
  useSetPermissions,
} from "../../hooks";
import { selectPermissions } from "../../features/permissionsSlice";
import { PlusIcon } from "../images";

export const Header = () => {
  const navigate = useNavigate();
  const {
    isAllowedEditCreateNewCampaign,
    isAllowedEditResyncData,
    isAllowedShareAccess,
  } = useAppSelector(selectPermissions);
  const isModalOpen = useAppSelector((state) => state.company.isModalOpen);
  const [isFetched, setIsFetched] = useState<boolean>(false);

  const { pathname } = useLocation();
  const { campaign_id } = useParams();

  const isAccountPlan =
    AppRoutes.ACCOUNT_PLAN.endsWith(pathname.split("/")[2]) ||
    pathname.includes("playbook");

  const scrollbarWidth = useScrollbarWidth({ trigger: isAccountPlan });

  const [getCampaignSummary, { data, error, currentData }] =
    useLazyGetCampaignSummaryQuery();
  const { data: user } = useGetUserQuery();
  useSetPermissions(currentData?.permissions ?? user?.permissions);

  const userRoles = useGetUserShareCampaignRoles(data?.user_roles);

  useNotFoundRedirect({ error });

  useEffect(() => {
    if (user && !isFetched) {
      void pushUserDataToGTM(user.email);
      setIsFetched(true);
    }
  }, [pathname, user]);

  useEffect(() => {
    if (campaign_id) {
      getCampaignSummary({ campaign_id });
    }
  }, [pathname]);


  return (
    <>
      <Stack
        sx={{
          height:
            data?.campaign_purpose === CampaignPurpose.demo ||
            (data?.user_roles && !userRoles.isEditor && !isAccountPlan)
              ? "100px"
              : "74px",
          background: "#F5F5F5",
          borderBottom: "1px solid #EAEAEA",
          boxShadow: "0px 5px 8.3px 0px #00000005",
          justifyContent: "center",
          position: "sticky",
          zIndex: 100,
          width: isModalOpen ? "100vw" : "100%",
          top: 0,
        }}
      >
        <Stack
          width="100%"
          sx={{
            px: { xs: 2, sm: 4, md: 5, xl: 9 },
          }}
        >
          <Toolbar style={{ padding: 0, justifyContent: "space-between" }}>
            <Link to={AppRoutes.CAMPAIGNS}>
              <CustomLogo />
            </Link>
            <Stack
              flexDirection="row"
              gap="16px"
              sx={{
                ...(isModalOpen && { paddingRight: `${scrollbarWidth}px` }),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                }}
              >
                <Stack flexDirection="row" gap="16px" alignItems="center">
                  {isAllowedShareAccess &&
                    user &&
                    campaign_id &&
                    ![CampaignPurpose.demo, CampaignPurpose.sales].some(
                      (el) => el === data?.campaign_purpose,
                    ) && <ShareCampaignBtn userEmail={user.email} />}
                  {campaign_id && data && isAllowedEditResyncData && (
                    <ResyncDataBtn
                      key={campaign_id}
                      campaign_id={campaign_id}
                      crmIntegration={data.crm}
                      mismatches={data.mismatches}
                      isDisabled={
                        [CampaignPurpose.demo, CampaignPurpose.sales].includes(
                          data.campaign_purpose,
                        ) || !isAllowedEditResyncData
                      }
                    />
                  )}

                  <ReuseButton
                    onClick={() => navigate(AppRoutes.CREATE_CAMPAIGN)}
                    buttonColor={ButtonsColorType.gradient}
                    buttonType={ButtonsType.contained}
                    disabled={!isAllowedEditCreateNewCampaign}
                    startIcon={<AddIcon fontSize="small" />}
                    sx={{ height: "40px" }}
                  >
                    Start New Campaign
                  </ReuseButton>
                </Stack>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ alignSelf: "stretch" }}
                />
              </Box>
              <Stack flexDirection="row" gap="8px">
                {campaign_id && <NotificationBtn campaign_id={campaign_id} />}
                <ProfileButton role={user?.roles[0]} />
              </Stack>
            </Stack>
          </Toolbar>
        </Stack>
        {campaign_id && data?.campaign_purpose === CampaignPurpose.demo && (
          <DemoBlock
            isDemo={true}
            paddingRight={isModalOpen ? `${scrollbarWidth}px` : undefined}
          />
        )}
        {campaign_id &&
          data?.user_roles &&
          !userRoles.isEditor &&
          data?.campaign_purpose !== CampaignPurpose.demo &&
          !isAccountPlan && (
            <DemoBlock
              isDemo={false}
              paddingRight={isModalOpen ? `${scrollbarWidth}px` : undefined}
            />
          )}
      </Stack>
    </>
  );
};
